// netlify/functions/lib/task-kinds.ts
//
// The canonical task-kind taxonomy. `tasks.kind` is set once at creation and is the ONLY thing that
// should be used to discriminate a task's type — NOT the human-readable `goal` string. (Historically
// these families had no column and were told apart by `goal LIKE 'Parallel harness:%'` etc., which
// also matched against a column that carries truncated user prose; `kind` replaces all of that.)
//
// This is a LEAF module: it imports nothing, so any file can import it without the circular-dependency
// dance the goal-prefix constants needed (their owning modules import tasks.ts). The goal-prefix
// constants still exist in their owning libs purely to MINT the human label; type checks use kind.
//
// Every task now carries a non-NULL kind. The FK columns (test_run_id/journey_id, exploration_id,
// version_id) still LINK a task to its row, but discrimination is by `kind` alone — including the
// browser-driver families (journey/exploration/version_added) that used to be FK-only + NULL kind.

export const TASK_KINDS = {
  HARNESS: 'harness',
  FILE_JOURNEY_BUG: 'file_journey_bug',
  RECORDING_FOLLOWUP: 'recording_followup',
  SECURITY_PROBE: 'security_probe',
  CLONE_BUILD: 'clone_build',
  ERROR_PROCESSING: 'error_processing',
  // One CRITIC RUN (proj-guidance): runs critic-run-script with no recording, grading the sample its run row
  // names. Started only from the admin critics page / MCP — nothing queues these on a schedule. Parallel, and
  // deliberately NOT deduped per critic: several runs of one critic (comparing versions) is the normal case.
  CRITIC_RUN: 'critic_run',
  ADVERSARY: 'adversary',
  // Browser-driver run families. These were historically identified by their FK column
  // (test_run_id/journey_id, exploration_id, version_id) and left kind NULL; they now carry an
  // explicit kind so no query has to special-case NULL. NOT parallel-eligible — one per container.
  JOURNEY: 'journey',
  EXPLORATION: 'exploration',
  VERSION_ADDED: 'version_added',
  // The ONE task every eval run starts with: runs src/guidance/evals/instantiate-script.ts (setup-only, no recording) in
  // the eval's dedicated project, which hands the eval's input to its kind's guidance module to set the
  // project up. Everything after that is ordinary project work. See lib/eval-projects.ts.
  EVAL_INSTANTIATE: 'eval_instantiate',
  // The eval's VERDICT phase: one more task at the project's idle edge, for a kind that can only judge
  // whether the eval passed once the project's work is done (missing-bug: the bugs it filed are the
  // evidence). Queued at most once per run. See lib/eval-verdict.ts.
  EVAL_VERDICT: 'eval_verdict',
  // PROCESSING an eval DEFINITION: decide what kind of eval a report is and build the input it runs against.
  // Runs in the GUIDANCE project (never the customer project the eval is about) and is recorded, because the
  // classification is a judgment and the blob is derived from a project's history — both need replaying when
  // they come out wrong. See lib/eval-process-tasks.ts.
  EVAL_PROCESS: 'eval_process',
} as const

export type TaskKind = (typeof TASK_KINDS)[keyof typeof TASK_KINDS]

/**
 * Adversaries run in the guidance project over a copied recording. The source project's entity ids
 * are evidence provenance, not ownership: attaching one here makes the guidance task appear to be
 * part of the customer's test run and lets run-scoped readers select the wrong task.
 */
export function assertValidTaskAssociations(task: {
  kind?: TaskKind | null
  testRunId?: string | null
  explorationId?: string | null
  journeyId?: string | null
  versionId?: string | null
}): void {
  if (task.kind !== TASK_KINDS.ADVERSARY) return
  if (task.testRunId || task.explorationId || task.journeyId || task.versionId) {
    throw new Error('Adversary tasks cannot be associated with project test runs, journeys, explorations, or versions')
  }
}

// One entry per kind, holding every property of it. Typed Record<TaskKind, KindSpec> so a kind added
// without an entry is a type error rather than a silently-wrong default.
//
//   parallel — drained by a project's "Parallel harness:" task(s); false means one task, one container.
//   driver   — the setup harness the container runs: run-recorded-mcp, run-recorded-browse, an agent
//              prompt, or nothing.
interface KindSpec {
  parallel: boolean
  driver: 'recording-mcp' | 'browse' | 'agent' | 'none'
}

const KIND_SPECS: Record<TaskKind, KindSpec> = {
  [TASK_KINDS.HARNESS]: { parallel: false, driver: 'none' }, // claims children, so cannot be one
  [TASK_KINDS.FILE_JOURNEY_BUG]: { parallel: true, driver: 'recording-mcp' },
  [TASK_KINDS.RECORDING_FOLLOWUP]: { parallel: true, driver: 'recording-mcp' },
  [TASK_KINDS.SECURITY_PROBE]: { parallel: false, driver: 'recording-mcp' },
  [TASK_KINDS.CLONE_BUILD]: { parallel: false, driver: 'recording-mcp' }, // too heavy to share a container
  [TASK_KINDS.ERROR_PROCESSING]: { parallel: false, driver: 'recording-mcp' },
  [TASK_KINDS.CRITIC_RUN]: { parallel: true, driver: 'recording-mcp' },
  [TASK_KINDS.ADVERSARY]: { parallel: true, driver: 'recording-mcp' },
  [TASK_KINDS.JOURNEY]: { parallel: false, driver: 'browse' },
  [TASK_KINDS.EXPLORATION]: { parallel: false, driver: 'browse' },
  [TASK_KINDS.VERSION_ADDED]: { parallel: false, driver: 'browse' },
  [TASK_KINDS.EVAL_INSTANTIATE]: { parallel: false, driver: 'recording-mcp' },
  [TASK_KINDS.EVAL_VERDICT]: { parallel: false, driver: 'recording-mcp' },
  [TASK_KINDS.EVAL_PROCESS]: { parallel: true, driver: 'recording-mcp' },
}

// Derived, not restated.
export const PARALLEL_ELIGIBLE_KINDS: TaskKind[] = (Object.keys(KIND_SPECS) as TaskKind[]).filter(
  (kind) => KIND_SPECS[kind].parallel,
)

/**
 * True when a task of this kind is drained by a project's parallel harness, i.e. runs as one of many
 * children SHARING that harness's container. Everything else — a clone build above all, which opens a
 * Replay session and moves tens of megabytes — gets a container to itself.
 *
 * The JS twin of the `kind = ANY(PARALLEL_ELIGIBLE_KINDS)` half of the dispatch predicate; the project's
 * `parallel_mcp_enabled` flag is the other half (see lib/parallel-tasks.ts isParallelEligibleTask). Also
 * the answer to "do this task's container logs belong to it?" — a harness child's stdout is the harness
 * process's stdout, so its container lines land on the harness task's page, not its own.
 */
export function isParallelEligibleKind(kind: string | null | undefined): boolean {
  return kind ? KIND_SPECS[kind as TaskKind]?.parallel === true : false
}

/** Which setup harness a container should run for this kind. Unknown/legacy NULL kinds get 'agent'. */
export function driverFor(kind: string | null | undefined): KindSpec['driver'] {
  const spec = kind ? KIND_SPECS[kind as TaskKind] : undefined
  return spec ? spec.driver : 'agent'
}

/** True when the container runs a followup_script over a recording (scripts/run-recorded-mcp.ts). */
export function runsRecordingDriver(kind: string | null | undefined): boolean {
  return driverFor(kind) === 'recording-mcp'
}

export function isRecordingFollowupKind(kind: string | null | undefined): boolean {
  return kind === TASK_KINDS.RECORDING_FOLLOWUP || kind === TASK_KINDS.SECURITY_PROBE
}
